import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { ModalPdfComponent } from '../../../generic/modal-pdf/modal-pdf.component';
import { environment } from '../../../../../environments/environment';
import { HttpRequestService } from '../../../../utilities/http/request.utilities';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { UploadFileComponent } from '../../../generic/upload-file/upload-file.component';
import { SerialRangeSelectionComponent } from '../../../generic/serial-range-selection/serial-range-selection.component';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../../generic/confirmation-dialog/confirmation-dialog.component';
import { UseResourcesDialogComponent } from '../../../generic/use-resources-dialog/use-resources-dialog.component';
import { Store } from '@ngrx/store';
import { UserState } from '../../../../store/user/user.model';
import { UseResourcesComponent } from '../../../generic/use-resources/use-resources.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { EquipmentService } from '../../../../services/equipment/equipment.service';

type OptionsConfig = {
  loading: BehaviorSubject<boolean>;
  error: BehaviorSubject<boolean>;
  enabled: BehaviorSubject<boolean>;
  data: BehaviorSubject<string[]>;
  label: BehaviorSubject<string>;
  filter: FormControl;
};

interface OptionsPresets {
  make: OptionsConfig;
  model: OptionsConfig;
  serialNumber: OptionsConfig;
  pvc: OptionsConfig;
}

const optionsPresets: OptionsPresets = {
  make: {
    loading: new BehaviorSubject(false),
    error: new BehaviorSubject(false),
    enabled: new BehaviorSubject(false),
    data: new BehaviorSubject<string[]>([]),
    label: new BehaviorSubject('Make'),
    filter: new FormControl({ value: '', disabled: true }),
  },
  model: {
    loading: new BehaviorSubject(false),
    error: new BehaviorSubject(false),
    enabled: new BehaviorSubject(false),
    data: new BehaviorSubject<string[]>([]),
    label: new BehaviorSubject('Model'),
    filter: new FormControl({ value: '', disabled: true }),
  },
  serialNumber: {
    loading: new BehaviorSubject(false),
    error: new BehaviorSubject(false),
    enabled: new BehaviorSubject(false),
    data: new BehaviorSubject<string[]>([]),
    label: new BehaviorSubject('Serial Number'),
    filter: new FormControl({ value: '', disabled: true }),
  },
  pvc: {
    loading: new BehaviorSubject(false),
    error: new BehaviorSubject(false),
    enabled: new BehaviorSubject(false),
    data: new BehaviorSubject<string[]>([]),
    label: new BehaviorSubject('PVC'),
    filter: new FormControl({ value: '', disabled: true }),
  },
};

@Component({
  selector: 'app-equipment-record',
  standalone: true,
  imports: [
    MatIcon,
    MatIconModule,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatDialogModule,
    MatMenuModule,
  ],
  templateUrl: './equipment-record.component.html',
  styleUrl: './equipment-record.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class EquipmentRecordComponent implements OnInit {
  maxPrompts = environment.maxPrompts;
  resources: any[] = [];
  filteredResources: any[] = []; 
  isEditing: boolean = false;
  optionsStatus: OptionsPresets = { ...optionsPresets };
  selectedOptions: Record<string, string | null> = {
    make: null,
    model: null,
    serialNumber: null,
    pvc: null,
  };
  downloadURL: string | null = null;
  newPrompt: string = '';
  isAdding: boolean = false;
  prompts: string[] = [];
  pvc: string = '';
  serialNumbers: string = '';
  serialStart: string = '';
  serialEnd: string = '';
  serialExclusions: string[] = [];
  relatedCombinations: any[] = [];
  pvcMakes: string[] = [];
  serialRanges: { from: string; to: string }[] = [];
  promptError: string = '';
  manualTypeOptions: string[] = [];
  filteredManualTypeOptions: string[] = [];
  manualTypeFilter = new FormControl([]); 
  manualTypeSearchCtrl = new FormControl(''); 

  constructor(
    public dialogRef: MatDialogRef<EquipmentRecordComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private httpRequestService: HttpRequestService,
    private store: Store<{ user: UserState }>,
    private snackBar: MatSnackBar,
    private sharedData: EquipmentService
  ) {}

  ngOnInit(): void {
    this.relatedCombinations = this.data.relatedCombinations || [];

    this.sharedData.sharedPVCMakes$.subscribe(value => {
      this.pvcMakes = value;
    });
    
    this.resources = this.relatedCombinations.flatMap((item: any) =>
      (item.pdf_link || [])
        .filter((resource: any) => {
          return (
            resource.url &&
            resource.url.endsWith('.pdf')&&
            !['Manual not available for Model', 'Manual not available for SN'].includes(resource.url)
          );
        })
        .map((resource: any) => ({
          title: resource.title,
          locale: resource.locale,
          manualType: resource.title,
          serialStart: item.startSerial || 'All',
          serialEnd: item.endSerial || 'All',
          pvc: item.pvc || '',
          serialExclusions: resource.serialExclusions || '',
          uploadedAt: new Date(resource.uploaded_at).toLocaleDateString(),
          url: resource.url,
          file_name: this.extractPdfName(resource.url) + '.pdf',
          equipment_make: item.make,
          equipment_model: item.model,
          prompts: item.prompts || [],
        }))
    );

    this.serialEnd = this.resources[0]?.serialEnd;
    this.serialStart = this.resources[0]?.serialStart;
    this.prompts = [...new Set(
      this.relatedCombinations.flatMap((item: any) => item.prompts || [])
    )];

  this.rebuildManualTypeOptions();
  this.filteredResources = [...this.resources];

  this.manualTypeFilter.valueChanges.subscribe(() => {
    this.applyManualTypeFilter();
  });

  this.manualTypeSearchCtrl.valueChanges.subscribe((search: string | null) => {
    const term = (search || '').toLowerCase();
    this.filteredManualTypeOptions = this.manualTypeOptions.filter(type =>
      type.toLowerCase().includes(term)
    );
  });

  }

   private rebuildManualTypeOptions(): void {
    const types = Array.from(
      new Set(
        (this.resources || [])
          .map(r => r.manualType)
          .filter((t: string | undefined) => !!t)
      )
    ) as string[];

    this.manualTypeOptions = types.sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
    this.filteredManualTypeOptions = [...this.manualTypeOptions];
  }
  
  private applyManualTypeFilter(): void {
  const selectedTypes: string[] = this.manualTypeFilter.value || [];

  let list = this.resources;

  if (selectedTypes.length > 0) {
    list = list.filter(r => selectedTypes.includes(r.manualType));
  }

  this.filteredResources = [...list];
}  

  getFilteredOptions(optionType: keyof OptionsPresets): string[] {
    const filterValue = this.optionsStatus[optionType].filter.value?.toLowerCase() || '';
    return this.optionsStatus[optionType].data.value.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }

  viewPDF(resource: any): void {
    console.log('View PDF:', resource.name);
  }

  removeResource(resource: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to delete the mapping between this manual and its associated serial number range?',
        fileName: resource.file_name,
        serialStart: resource.serialStart,
        serialEnd: resource.serialEnd,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      const endpoint = environment.integrations.deleteResource;
      const payload = {
        file_name: resource.file_name?.replace(/\.pdf$/, '') || resource.fileName?.replace(/\.pdf$/, ''),
        make: resource.equipment_make || this.data.make,
        model: resource.equipment_code || this.data.model,
        serialStart: resource.serialStart,
        serialEnd: resource.serialEnd,
        pvc: resource.pvc,
        serialExclusions: resource.serialExclusions,
        title: resource.title,
      };

      if (!payload.file_name || !payload.make || !payload.model || !payload.serialStart || !payload.serialEnd) {
        console.error('Invalid payload:', payload);
        alert('Unable to remove resource. Ensure all required data is provided.');
        return;
      }

      this.httpRequestService.request<any>('DELETE', endpoint, payload).subscribe({
        next: () => {
          this.resources = this.resources.filter((res) => res !== resource);
          this.rebuildManualTypeOptions();
          this.applyManualTypeFilter();
        },
        error: (error) => {
          console.error('Error removing resource:', error);
          alert('Failed to remove resource. Please try again.');
        },
      });
    });
  }

openUploadModal(): void {
  if (this.resources.length === 0) {
    const confirmDialog = this.dialog.open(UseResourcesDialogComponent, {
      width: '400px',
      disableClose: true, // prevent closing until we decide
      data: {
        message: 'No resources found for this model. Do you want to use resources from another model?',
        yesLabel: 'Yes',
        noLabel: 'No',
      }
    });

    confirmDialog.afterClosed().subscribe((confirmed: boolean | undefined) => {
      if (confirmed === true) {
        this.httpRequestService.request<any>(
          'GET',
          `${environment.integrations.adminResources}?make=${this.data.make}`
        ).subscribe({
          next: (response) => {
            const items = response.items || [];

            const modelsWithValidManuals = items
              .filter((item: any) =>
                item.MAKE === this.data.make &&
                item.Models !== this.data.model &&
                Array.isArray(item.Manuals) &&
                item.Manuals.some((manual: any) =>
                  manual.url &&  
                  manual.url.endsWith('.pdf')&&
                  !manual.url.includes('Manual not available')
                )
              );

              if (modelsWithValidManuals.length === 0) {
                  this.snackBar.open('No usable resources found in other models under this make.', 'Dismiss', {
                  duration: 5000,
                  panelClass: ['custom-center-snackbar'],
                  horizontalPosition: 'center',
                  verticalPosition: 'top'
                });
                  // return;
           } else {
              const useResDialog = this.dialog.open(UseResourcesComponent, {
                width: '600px',
                data: {
                  make: this.data.make,
                  model: this.data.model,
                }
              });

              useResDialog.afterClosed().subscribe(() => {
                this.refreshResourcesFromAdminAPI();
              });
            }

          },
          error: (error) => {
            console.error('Failed to check available resources:', error);
          }
        });
      } else if (confirmed === false) {
        this.launchSerialRangeSelection();
      } else {
        console.log('Dialog cancelled, no action taken.');
      }
    });
  } else {
    this.launchSerialRangeSelection();
  }
}

private launchSerialRangeSelection(): void {
  const serialDialog = this.dialog.open(SerialRangeSelectionComponent, {
    width: '600px',
    data: {
      serialStart: this.serialStart,
      serialEnd: this.serialEnd,
      make: this.data.make,
    }
  });

  serialDialog.afterClosed().subscribe((result) => {
    if (result?.serialRanges?.length > 0) {
      this.serialRanges = result.serialRanges;
      this.serialExclusions = result.serialExclusions || [];
      this.pvc = result.pvc ? (result.pvc.endsWith('.0') ? result.pvc : `${result.pvc}.0`) : '';
      const uploadDialog = this.dialog.open(UploadFileComponent, {
        width: '100px',
        height: '350px',
        data: {
          make: this.data.make,
          model: this.data.model,
          serialRanges: this.serialRanges,
          serialExclusions: this.serialExclusions,
          pvc: this.pvc,
          prompts: this.prompts,
          serialNumbers: this.serialNumbers,
          existingResources: this.resources
        }
      });

      uploadDialog.afterClosed().subscribe(() => {
        this.refreshResourcesFromAdminAPI();
      });
    }
  });
}

  removePrompt(index: number): void {
    const promptToRemove = this.prompts[index];
    this.promptError = '';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        message: 'Are you sure you want to remove the prompt?',
        prompt: promptToRemove,
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) return;
      try {
        await Promise.all(this.relatedCombinations.map(item =>
          lastValueFrom(
            this.httpRequestService
              .request<any>('DELETE', `${environment.integrations.deleteCommonPrompt}`, {
                make: item.make,
                model: item.model,
                common_prompt: promptToRemove,
              })
              .pipe(take(1))
          ))
        );
        this.prompts.splice(index, 1);
        this.refreshResourcesFromAdminAPI();
      } catch (error) {
        console.error('Error deleting prompts:', error);
        alert('Failed to delete prompt. Please try again.');
      }
    });
  }

  addPrompt(): void {
    if (this.prompts.length < this.maxPrompts){
      this.isAdding = true;
      this.newPrompt = '';
    }
    else{
      this.promptError = `You\'ve reached the limit of ${this.maxPrompts} active prompts for this equipment model. To add a new one, please remove an existing prompt first.`
    } 
  }

  async saveNewPrompt(): Promise<void> {
    this.isAdding = false;
    const trimmedPrompt = this.newPrompt?.trim();
    if (!trimmedPrompt) return;
    const isDuplicate = this.prompts.some(p => p.toLowerCase() === trimmedPrompt.toLowerCase());
    if (isDuplicate) {
      alert('This prompt already exists. Please try another one.');
      return;
    }

    try {
      const userState = await lastValueFrom(this.store.select('user').pipe(take(1)));
      const email = userState.userInfo?.email;
      const uploaded_by = email ? email.split('@')[0] : 'anonymous';
      await Promise.all(this.relatedCombinations.map(item =>
        lastValueFrom(
          this.httpRequestService.request<any>('PUT', `${environment.integrations.addCommonPrompt}`, {
            make: item.make,
            model: item.model,
            common_prompt: trimmedPrompt,
            uploaded_by: uploaded_by
          }).pipe(take(1))
        )
      ));
      this.refreshResourcesFromAdminAPI();
      this.newPrompt = '';
    } catch (error) {
      console.error('Error saving prompts:', error);
    }
  }

  closeModal(): void {
    this.dialogRef.close({
      selectedMake: this.selectedOptions['make'],
    });
  }

  public openPDFModal(pdfUrl: string): void {
    const fileName = this.extractPdfName(pdfUrl) + '.pdf';
    const manualType = pdfUrl.split('/').slice(-2, -1)[0];
    const documentMake = pdfUrl.split('/').slice(-3, -2)[0];
    this.downloadURL = `${environment.integrations.manualDownload}?equipment_make=${documentMake}&file_name=${encodeURIComponent(fileName)}&manual_type=${manualType}`;
    if (this.downloadURL) {
      this.fetchDownloadUrl(this.downloadURL)
        .then((resource) => {
          this.dialog.open(ModalPdfComponent, {
            data: { pdfUrl: resource.downloadUrl },
          });
        })
        .catch((error) => {
          console.error('Error fetching download URL:', error);
        });
    } else {
      console.error('Uploaded URL is null.');
    }
  }

  private fetchDownloadUrl(providerResourceUrl: string): Promise<{ downloadUrl: string; s3Key: string }> {
    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', providerResourceUrl).subscribe({
        next: (response) => resolve({ downloadUrl: response.download_url, s3Key: response.s3_key }),
        error: (error) => reject(error),
      });
    });
  }

  extractPdfName(url: string): string {
    const match = url.match(/\/([^/]+)\.pdf$/);
    return match ? match[1] : '';
  }

  refreshResourcesFromAdminAPI(): void {
    const endpoint = `${environment.integrations.adminResources}?make=${this.data.make}`; 

    this.httpRequestService.request<any>('GET', endpoint).subscribe({
      next: (response) => {
        
        const allItems = response.items || [];

        this.relatedCombinations = allItems
          .filter((item: any) =>
            item.MAKE === this.data.make && item.Models === this.data.model
          )
          .map((item: any) => ({
            make: item.MAKE,
            model: item.Models,
            startSerial: item.StartSerial,
            endSerial: item.EndSerial,
            pdf_link: item.Manuals,
            pvc: item.PVC,
            prompts: Array.isArray(item.PROMPTS?.resources)
              ? item.PROMPTS.resources.map((prompt: any) => prompt.prompt)
              : [],
          }));
        this.resources = this.relatedCombinations.flatMap((item: any) =>
          (item.pdf_link || [])
            .filter((resource: any) => {
              return (
                resource.url &&
                resource.url.endsWith('.pdf')  &&
                !['Manual not available for Model', 'Manual not available for SN'].includes(resource.url)
              );
            })
            .map((resource: any) => ({
              title: resource.title,
              locale: resource.locale,
              manualType: resource.title,
              serialStart: item.startSerial || 'All',
              serialEnd: item.endSerial || 'All',
              pvc: item.pvc || '',
              serialExclusions: resource.serialExclusions || '',
              uploadedAt: new Date(resource.uploaded_at || new Date()).toLocaleDateString(),
              url: resource.url,
              file_name: this.extractPdfName(resource.url) + '.pdf',
              equipment_make: item.make,
              equipment_model: item.model,
              prompts: item.prompts || [],
            }))
        );

        this.rebuildManualTypeOptions();
        this.applyManualTypeFilter();
        
        this.prompts = [...new Set(this.relatedCombinations.flatMap((item: any) => item.prompts || []))];
      },
      error: (error) => {
        console.error('Error fetching resources from admin API:', error);
      },
    });
  }
}
